// 方案與帳務域顯示層模型(S33–S36)。狀態 enum 值來自後端 wire,顯示層全中文;
// 金額 minor units → Intl 貨幣格式;版本 diff 與 grant 來源鏈展開皆為前端純函式,
// 便於單測。RBAC:依 IA 定案 §3.2,本域寫入 = platform-owner + admin(security-officer
// 可作 checker、不可作 maker);前端守門只影響 UI 呈現,伺服端 requireAdminWrite 仍是真正邊界。

import type { PlanDefinition, PlanEntitlements, PlanGrant } from './commerceApi';

// ---- 域級寫守門(IA specs/01 §3.1/§3.2:CanCommerceWrite 目標矩陣) ----

export function canCommerceWrite(role: string | null | undefined): boolean {
  return role === 'admin' || role === 'platform-owner';
}

export function canCommerceApprove(role: string | null | undefined): boolean {
  return role === 'admin' || role === 'platform-owner' || role === 'security-officer';
}

// ---- dual-control:durable operation + different checker + password step-up ----

export const DUAL_CONTROL_NOTE =
  '此為高風險操作：送出後只建立 24 小時待核准單，必須由不同的管理員以自己的密碼 step-up 核准才會執行。';

// ---- failure_code:status 本身不足以決定該怎麼處置 ----
//
// restful-api!422/!429 之後,`failed` 與 `completed` 各多了一個「字面意思會誤導」
// 的情況,而處置方式正好相反,所以 status 不能單獨呈現:
//
//   failed + EXECUTION_INDETERMINATE   動作**可能已經執行**(reaper 收斂卡在
//                                      processing 的單)。直接重做 =
//                                      adjustment.post 雙重記帳 / 重複抹除。
//   completed + RESULT_PERSIST_FAILED  動作**確實成功**,只是結果內容沒存下來。
//
// 後端 openapi 對這兩個碼都寫了處置指示;這裡是把它送到人眼前的地方。

export const COMMERCE_FAILURE_INDETERMINATE = 'EXECUTION_INDETERMINATE';
export const COMMERCE_FAILURE_RESULT_PERSIST = 'RESULT_PERSIST_FAILED';

export interface CommerceOperationNotice {
  tone: 'is-err' | 'is-warn' | 'is-ok';
  text: string;
}

/** 依 status + failure_code 給出該顯示的處置說明;沒有值得說的就回 null。 */
export function operationNotice(
  op: { status?: string; failure_code?: string } | null | undefined
): CommerceOperationNotice | null {
  if (!op) return null;
  switch (op.failure_code) {
    case COMMERCE_FAILURE_INDETERMINATE:
      return {
        tone: 'is-err',
        text:
          '⚠️ 動作是否已執行不明：此單逾時卡在執行中，由系統收斂為 failed。' +
          'failed 只代表「未確認完成」，不代表「未執行」——' +
          '請先人工核對實際結果（帳目、抹除紀錄等），不得直接重做。',
      };
    case COMMERCE_FAILURE_RESULT_PERSIST:
      return {
        tone: 'is-warn',
        text: '動作已成功執行，但結果內容未能寫入紀錄（result 從缺）。動作本身無需重做。',
      };
    default:
      break;
  }
  if (op.status === 'failed') {
    return { tone: 'is-warn', text: `動作未執行成功${op.failure_code ? `（${op.failure_code}）` : ''}，可重新建立單據。` };
  }
  return null;
}

/** status pill 的色調。indeterminate 需要人工介入,不能與一般失敗同色。 */
export function operationStatusTone(op: { status?: string; failure_code?: string } | null | undefined): string {
  if (!op) return 'pill-neutral';
  if (op.failure_code === COMMERCE_FAILURE_INDETERMINATE) return 'pill-bad';
  if (op.status === 'completed') return 'pill-good';
  if (op.status === 'failed') return 'pill-warn';
  return 'pill-info';
}

// ---- 狀態 / enum 顯示 ----

export function planStatusLabel(status?: string): string {
  if (status === 'draft') return '草稿';
  if (status === 'published') return '已發布';
  if (status === 'retired') return '已停售';
  return status || '未知';
}

export function planStatusTone(status?: string): string {
  if (status === 'published') return 'pill-good';
  if (status === 'draft') return 'pill-info';
  if (status === 'retired') return 'pill-neutral';
  return 'pill-neutral';
}

export function audienceLabel(audience?: string): string {
  if (audience === 'consumer') return '消費者';
  if (audience === 'business') return '企業';
  if (audience === 'partner') return '夥伴';
  if (audience === 'internal') return '內部';
  return audience || '—';
}

export function billingIntervalLabel(interval?: string): string {
  if (interval === 'day') return '日繳';
  if (interval === 'week') return '週繳';
  if (interval === 'month') return '月繳';
  if (interval === 'year') return '年繳';
  if (interval === 'one_time') return '一次性';
  return interval || '—';
}

export function grantStatusLabel(status?: string): string {
  if (status === 'active') return '生效中';
  if (status === 'revoked') return '已撤銷';
  return status || '未知';
}

export function grantSourceLabel(source?: string): string {
  if (source === 'promotion') return '行銷活動';
  if (source === 'support') return '客服補償';
  if (source === 'partner') return '夥伴方案';
  if (source === 'admin') return '管理台贈與';
  if (source === 'migration') return '遷移轉入';
  return source || '—';
}

export function stackingLabel(policy?: string): string {
  if (policy === 'additive') return '疊加';
  if (policy === 'max') return '取最大';
  if (policy === 'replace') return '取代';
  return policy || '—';
}

export function subjectTypeLabel(type?: string): string {
  if (type === 'account') return '帳號';
  if (type === 'device') return '裝置';
  if (type === 'device_group') return '裝置群組';
  if (type === 'org') return '組織';
  return type || '—';
}

export function reconItemStatusLabel(item: { status?: string; resolution?: string }): string {
  if (item.status === 'open') return '待處理';
  if (item.status === 'investigating') return '調查中';
  if (item.status === 'resolved') {
    if (item.resolution === 'adjusted') return '已調帳結案';
    if (item.resolution === 'accepted') return '接受差異結案';
    return '已結案';
  }
  return item.status || '未知';
}

export function reconItemStatusTone(item: { status?: string; resolution?: string }): string {
  if (item.status === 'open') return 'pill-warn';
  if (item.status === 'investigating') return 'pill-info';
  if (item.status === 'resolved') return 'pill-good';
  return 'pill-neutral';
}

export function ledgerDirectionLabel(direction?: string): string {
  if (direction === 'debit') return '借';
  if (direction === 'credit') return '貸';
  return direction || '—';
}

// ---- 金額格式(minor units + ISO-4217;小數位依幣別,JPY=0、USD=2) ----

export function formatMinor(amountMinor: number | null | undefined, currency?: string): string {
  if (amountMinor === null || amountMinor === undefined || !Number.isFinite(amountMinor)) return '—';
  const cur = (currency ?? '').trim().toUpperCase();
  if (!/^[A-Z]{3}$/.test(cur)) return String(amountMinor);
  try {
    const fmt = new Intl.NumberFormat('zh-TW', { style: 'currency', currency: cur, currencyDisplay: 'code' });
    const digits = fmt.resolvedOptions().maximumFractionDigits ?? 2;
    return fmt.format(amountMinor / 10 ** digits);
  } catch {
    return `${amountMinor} ${cur}`;
  }
}

/** 帶正負號的差額顯示(對帳 delta 用)。 */
export function formatSignedMinor(amountMinor: number | null | undefined, currency?: string): string {
  if (amountMinor === null || amountMinor === undefined || !Number.isFinite(amountMinor)) return '—';
  const abs = formatMinor(Math.abs(amountMinor), currency);
  if (amountMinor > 0) return `+${abs}`;
  if (amountMinor < 0) return `-${abs}`;
  return abs;
}

// ---- datetime-local ↔ µs(表單輸入用;空字串 ↔ undefined) ----

export function usFromLocalInput(value: string): number | undefined {
  if (!value.trim()) return undefined;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms * 1000 : undefined;
}

export function localInputFromUs(us?: number): string {
  if (!us || us <= 0) return '';
  const d = new Date(us / 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ---- entitlement 欄位目錄(diff / 來源鏈展開共用) ----

export type EntitlementKind = 'bool' | 'int' | 'text' | 'list';

export interface EntitlementFieldDef {
  key: keyof PlanEntitlements;
  label: string;
  kind: EntitlementKind;
}

export const ENTITLEMENT_FIELDS: EntitlementFieldDef[] = [
  { key: 'recording_enabled', label: '雲端錄影', kind: 'bool' },
  { key: 'recording_modes', label: '錄影模式', kind: 'list' },
  { key: 'retention_days', label: '錄影保留(天)', kind: 'int' },
  { key: 'quality_max', label: '最高畫質', kind: 'text' },
  { key: 'locations', label: '儲存位置', kind: 'list' },
  { key: 'storage_gb', label: '儲存空間(GB)', kind: 'int' },
  { key: 'event_retention_days', label: '事件保留(天)', kind: 'int' },
  { key: 'relay_gb', label: '中繼流量(GB)', kind: 'int' },
  { key: 'device_count', label: '裝置數上限', kind: 'int' },
  { key: 'camera_count', label: '攝影機數上限', kind: 'int' },
  { key: 'channel_count', label: '通道數上限', kind: 'int' },
  { key: 'share_users', label: '分享人數上限', kind: 'int' },
  { key: 'ai_features', label: 'AI 功能', kind: 'list' },
  { key: 'ai_monthly_units', label: 'AI 月額度', kind: 'int' },
  { key: 'firmware_channels', label: '韌體通道', kind: 'list' },
];

export function emptyEntitlements(): PlanEntitlements {
  return {
    // 全新方案的 Ops 軸預設關閉。這是「顯式的 false」不是「沒說」——
    // 後端要求三欄一律顯式送出(S33 靜默歸零的修法)。
    ops_enabled: false,
    max_seats: 0,
    max_devices: 0,
    recording_enabled: false,
    recording_modes: [],
    retention_days: 0,
    quality_max: '',
    locations: [],
    storage_gb: 0,
    event_retention_days: 0,
    relay_gb: 0,
    device_count: 0,
    camera_count: 0,
    channel_count: 0,
    share_users: 0,
    ai_features: [],
    ai_monthly_units: 0,
    firmware_channels: [],
  };
}

export function formatEntitlementValue(def: EntitlementFieldDef, entitlements?: PlanEntitlements): string {
  const value = entitlements?.[def.key];
  if (def.kind === 'bool') return value ? '啟用' : '停用';
  if (def.kind === 'list') {
    const list = Array.isArray(value) ? (value as string[]) : [];
    return list.length > 0 ? list.join('、') : '—';
  }
  if (def.kind === 'int') {
    const n = typeof value === 'number' ? value : 0;
    return String(n);
  }
  return typeof value === 'string' && value ? value : '—';
}

// ---- 版本 diff(S33:entitlements / limits / 區域價格) ----

export interface PlanDiffRow {
  key: string;
  label: string;
  a: string;
  b: string;
  changed: boolean;
}

function priceLines(plan: PlanDefinition): string {
  const prices = Array.isArray(plan.prices) ? plan.prices : [];
  if (prices.length === 0) return '—';
  return prices
    .map((p) => `${p.region} ${formatMinor(p.amount_minor, p.currency)} / ${billingIntervalLabel(p.billing_interval)}`)
    .sort()
    .join('\n');
}

/** 兩版本欄位級 diff;a=基準、b=比較。頂層欄 + 全部 entitlement 欄 + 價格書。 */
export function diffPlanVersions(a: PlanDefinition, b: PlanDefinition): PlanDiffRow[] {
  const rows: PlanDiffRow[] = [];
  const top: Array<{ key: string; label: string; read: (p: PlanDefinition) => string }> = [
    { key: 'name', label: '名稱', read: (p) => p.name || '—' },
    { key: 'status', label: '狀態', read: (p) => planStatusLabel(p.status) },
    { key: 'audience', label: '客群', read: (p) => audienceLabel(p.audience) },
    { key: 'family_id', label: '品牌家族', read: (p) => p.family_id || '—' },
    { key: 'billing_interval', label: '計費週期', read: (p) => billingIntervalLabel(p.billing_interval) },
    { key: 'purchasable', label: '可購買', read: (p) => (p.purchasable ? '是' : '否') },
  ];
  for (const t of top) {
    const va = t.read(a);
    const vb = t.read(b);
    rows.push({ key: t.key, label: t.label, a: va, b: vb, changed: va !== vb });
  }
  for (const def of ENTITLEMENT_FIELDS) {
    const va = formatEntitlementValue(def, a.entitlements);
    const vb = formatEntitlementValue(def, b.entitlements);
    rows.push({ key: String(def.key), label: def.label, a: va, b: vb, changed: va !== vb });
  }
  const pa = priceLines(a);
  const pb = priceLines(b);
  rows.push({ key: 'prices', label: '區域價格', a: pa, b: pb, changed: pa !== pb });
  return rows;
}

// ---- grant 來源鏈展開(S34) ----

export type GrantEffectiveState = 'active' | 'scheduled' | 'expired' | 'revoked';

export function grantEffectiveState(grant: PlanGrant, nowUs: number): GrantEffectiveState {
  if (grant.status === 'revoked') return 'revoked';
  if (grant.effective_from && grant.effective_from > nowUs) return 'scheduled';
  if (grant.effective_to && grant.effective_to > 0 && grant.effective_to <= nowUs) return 'expired';
  return 'active';
}

export function grantEffectiveStateLabel(state: GrantEffectiveState): string {
  if (state === 'active') return '生效中';
  if (state === 'scheduled') return '尚未生效';
  if (state === 'expired') return '已到期';
  return '已撤銷';
}

export function grantEffectiveStateTone(state: GrantEffectiveState): string {
  if (state === 'active') return 'pill-good';
  if (state === 'scheduled') return 'pill-info';
  return 'pill-neutral';
}

export interface GrantSource {
  grant: PlanGrant;
  plan?: PlanDefinition; // catalog 內對應 plan@version(找不到=版本已不在 catalog 回傳中)
}

/**
 * 商業層(Plan/grant)參考合成——僅供解釋鏈檢視。依 created_at 順序套用各 grant 的
 * stacking policy:replace=整組取代、additive=數值相加/布林 OR/清單聯集、max=數值取大。
 * ⚠️ 這是前端估算:最終 effective 由後端 resolver 疊 AID ceiling + policy 裁決(common/12),
 * admin 尚無 effective-resolve 端點,UI 必須明示此限制。
 */
export function composeCommercialEntitlements(sources: GrantSource[]): PlanEntitlements | null {
  const applicable = sources.filter((s) => s.plan?.entitlements);
  if (applicable.length === 0) return null;
  const ordered = [...applicable].sort((x, y) => (x.grant.created_at || 0) - (y.grant.created_at || 0));
  let acc = emptyEntitlements();
  for (const { grant, plan } of ordered) {
    const e = plan!.entitlements;
    if (grant.stacking_policy === 'replace') {
      acc = { ...emptyEntitlements(), ...e };
      continue;
    }
    const max = grant.stacking_policy === 'max';
    const num = (a: number, b: number) => (max ? Math.max(a, b) : a + b);
    const list = (a: string[], b: string[]) => Array.from(new Set([...(a ?? []), ...(b ?? [])]));
    acc = {
      // Ops 軸沿用堆疊語意:開通取 OR、上限取 max/sum(與其餘數值欄一致)。
      ops_enabled: acc.ops_enabled || e.ops_enabled,
      max_seats: num(acc.max_seats ?? 0, e.max_seats ?? 0),
      max_devices: num(acc.max_devices ?? 0, e.max_devices ?? 0),
      recording_enabled: acc.recording_enabled || e.recording_enabled,
      recording_modes: list(acc.recording_modes, e.recording_modes),
      retention_days: num(acc.retention_days, e.retention_days ?? 0),
      quality_max: e.quality_max || acc.quality_max,
      locations: list(acc.locations, e.locations),
      storage_gb: num(acc.storage_gb, e.storage_gb ?? 0),
      event_retention_days: num(acc.event_retention_days, e.event_retention_days ?? 0),
      relay_gb: num(acc.relay_gb, e.relay_gb ?? 0),
      device_count: num(acc.device_count, e.device_count ?? 0),
      camera_count: num(acc.camera_count, e.camera_count ?? 0),
      channel_count: num(acc.channel_count, e.channel_count ?? 0),
      share_users: num(acc.share_users, e.share_users ?? 0),
      ai_features: list(acc.ai_features, e.ai_features),
      ai_monthly_units: num(acc.ai_monthly_units, e.ai_monthly_units ?? 0),
      firmware_channels: list(acc.firmware_channels, e.firmware_channels),
    };
  }
  return acc;
}

// ---- ledger 帳戶簿記(S35 查詢建議;wire 上已知的帳戶名) ----

export const KNOWN_LEDGER_ACCOUNTS = [
  'asset:cash',
  'contra_revenue:plan_grants',
  'contra_revenue:refunds',
  'expense:customer_credits',
  'expense:plan_grants',
  'liability:customer_credits',
];

/** adjustment 分錄借貸平衡檢查(送出前前端擋;後端 ValidateLedgerTransaction 仍是權威)。 */
export function adjustmentBalance(entries: Array<{ direction: string; amount_minor: number }>): {
  debit: number;
  credit: number;
  balanced: boolean;
} {
  let debit = 0;
  let credit = 0;
  for (const e of entries) {
    if (!Number.isFinite(e.amount_minor) || e.amount_minor < 0) return { debit, credit, balanced: false };
    if (e.direction === 'debit') debit += e.amount_minor;
    else if (e.direction === 'credit') credit += e.amount_minor;
    else return { debit, credit, balanced: false };
  }
  return { debit, credit, balanced: entries.length >= 2 && debit === credit && debit > 0 };
}

// ---- 共用:清單字串解析(逗號/換行) ----

export function parseList(raw: string): string[] {
  return raw
    .split(/[\n,、]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export const PLAN_ID_PATTERN = /^[a-z0-9][a-z0-9-]{1,63}$/;

/**
 * Ops 軸三欄。後端 domain.PlanEntitlements 這三欄沒有 `omitempty`,payload 缺席
 * 即解碼成 false/0/0 —— 由既有 Ops 方案衍生新版本會因此把 Ops 靜默關掉,publish
 * 後整租戶 /v3/ops fail-closed(admin 00 §3.7 S33)。
 */
export const OPS_ENTITLEMENT_KEYS = ['ops_enabled', 'max_seats', 'max_devices'] as const;

/**
 * 回報 entitlements payload 中缺席(undefined)的 Ops 軸欄位。
 *
 * 這是伺服端 400 的前端鏡像,存在的理由是**位置**:草稿是在瀏覽器組出來的,
 * 而缺欄的後果(整租戶掉線)要等到 publish 之後才看得見。在送出前就擋住,
 * 錯誤才指得到真正的原因。注意判準是 `undefined` 而不是 falsy —— 顯式的
 * `false`/`0` 是合法的「我就是要關」,與「沒說」必須分開。
 */
export function missingOpsEntitlementKeys(e: Partial<PlanEntitlements>): string[] {
  return OPS_ENTITLEMENT_KEYS.filter((key) => e[key] === undefined);
}
